<template>
  <div class="attendance-tab">
    <div class="attendance-tab__intro">
      <div class="text-h6 font-weight-bold">Chamada da turma</div>
      <div class="attendance-tab__hint">
        Escolha o mês e a aula para registrar a presença de todos os alunos de
        uma vez. Aulas futuras ficam bloqueadas.
      </div>
    </div>

    <v-progress-linear
      v-if="loading"
      indeterminate
      rounded
      height="4"
      class="mt-4"
      :color="theme.primary"
    />

    <v-alert v-else-if="!members.length" type="info" text class="mt-4 mb-0">
      A chamada só pode ser feita com alunos ativos na turma.
    </v-alert>

    <v-alert
      v-else-if="!availableMonths.length"
      type="info"
      text
      class="mt-4 mb-0"
    >
      Nenhuma aula desta turma foi encontrada no calendário ainda.
    </v-alert>

    <template v-else>
      <v-row dense class="mt-3">
        <v-col cols="12" md="5">
          <v-select
            v-model="selectedMonthKey"
            :items="monthOptions"
            item-text="text"
            item-value="value"
            label="Mês de referência"
            outlined
            dense
            hide-details="auto"
            :disabled="loading"
            @change="handleMonthChange"
          />
        </v-col>

        <v-col cols="12" md="7">
          <v-select
            v-model="selectedLessonId"
            :items="lessonOptions"
            item-text="text"
            item-value="value"
            label="Aula"
            outlined
            dense
            hide-details="auto"
            :disabled="loading || !lessons.length"
            @change="handleLessonChange"
          />
        </v-col>
      </v-row>

      <v-alert v-if="!lessons.length" type="info" text class="mt-4 mb-0">
        Nenhuma aula encontrada para o mês selecionado.
      </v-alert>

      <template v-else-if="selectedLesson">
        <div class="attendance-tab__lesson-head mt-5">
          <div>
            <div class="attendance-tab__lesson-title">
              {{ selectedLesson.title || "Aula" }}
            </div>
            <div class="attendance-tab__lesson-date">
              {{ formatLessonDate(selectedLesson.eventDate) }} ·
              {{ selectedLesson.startsAt }}
            </div>
          </div>

          <v-chip v-if="selectedLesson.isFuture" small outlined color="warning">
            <v-icon x-small left>mdi-clock-outline</v-icon>
            Aula futura
          </v-chip>

          <div v-else class="attendance-tab__quick">
            <v-btn x-small text rounded color="#16a34a" @click="markAll(1)">
              Todos presentes
            </v-btn>
            <v-btn x-small text rounded color="#ef4444" @click="markAll(2)">
              Todos falta
            </v-btn>
          </div>
        </div>

        <v-row dense class="mt-3">
          <v-col cols="12" md="8">
            <v-select
              v-model="lessonSubjectId"
              :items="subjectOptions"
              item-text="title"
              item-value="id"
              label="Assunto do curso desta aula"
              prepend-inner-icon="mdi-book-open-variant"
              outlined
              dense
              hide-details="auto"
              :loading="loadingSubjects || savingSubject"
              :disabled="!subjectOptions.length || savingSubject"
              @change="changeSubject"
            />
          </v-col>
        </v-row>

        <div
          v-if="selectedLesson.courseClassId && !selectedLesson.isFuture"
          class="attendance-tab__course-hint mt-3"
        >
          <v-icon x-small color="#16a34a" class="mr-1">
            mdi-check-decagram-outline
          </v-icon>
          Ao salvar, os alunos presentes têm este assunto marcado como concluído
          na aba Progresso.
        </div>

        <v-alert
          v-if="!rosterMembers.length"
          type="info"
          text
          class="mt-4 mb-0"
        >
          Nenhum aluno ativo está vinculado a esta aula.
        </v-alert>

        <div v-else class="attendance-tab__roster mt-4">
          <div
            v-for="student in rosterMembers"
            :key="student.id"
            class="attendance-row"
          >
            <div class="d-flex align-center">
              <v-avatar
                size="40"
                class="mr-3"
                :color="student.avatar ? '' : theme.primary"
              >
                <v-img v-if="student.avatar" :src="student.avatar" cover />
                <span v-else class="white--text attendance-row__initials">
                  {{ initials(student.name) }}
                </span>
              </v-avatar>
              <div>
                <div class="attendance-row__name">
                  {{ student.name || "Aluno(a)" }}
                </div>
                <div
                  v-if="selectedLesson.courseClassId"
                  class="attendance-row__subject"
                  :class="{
                    'attendance-row__subject--done':
                      savedStatusOf(student.id) === 1,
                  }"
                >
                  <v-icon x-small class="mr-1">
                    {{
                      savedStatusOf(student.id) === 1
                        ? "mdi-check-circle"
                        : "mdi-circle-outline"
                    }}
                  </v-icon>
                  {{
                    savedStatusOf(student.id) === 1
                      ? "Assunto concluído"
                      : "Não concluído"
                  }}
                </div>
              </div>
            </div>

            <div class="attendance-row__toggle">
              <v-btn
                small
                rounded
                class="attendance-row__btn"
                :depressed="statusOf(student.id) === 1"
                :outlined="statusOf(student.id) !== 1"
                :color="statusOf(student.id) === 1 ? '#16a34a' : '#94a3b8'"
                :dark="statusOf(student.id) === 1"
                :disabled="selectedLesson.isFuture"
                @click="setStatus(student.id, 1)"
              >
                <v-icon x-small left>mdi-check</v-icon>
                Presente
              </v-btn>

              <v-btn
                small
                rounded
                class="attendance-row__btn ml-2"
                :depressed="statusOf(student.id) === 2"
                :outlined="statusOf(student.id) !== 2"
                :color="statusOf(student.id) === 2 ? '#ef4444' : '#94a3b8'"
                :dark="statusOf(student.id) === 2"
                :disabled="selectedLesson.isFuture"
                @click="setStatus(student.id, 2)"
              >
                <v-icon x-small left>mdi-close</v-icon>
                Falta
              </v-btn>
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap justify-end align-center mt-6">
          <div v-if="isDirty" class="caption mr-4 attendance-tab__unsaved">
            Há alterações não salvas nesta aula.
          </div>

          <v-btn
            rounded
            :color="theme.primary"
            class="white--text"
            :loading="saving"
            :disabled="!isDirty || selectedLesson.isFuture"
            @click="save"
          >
            Salvar chamada
          </v-btn>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import { initialsFromName } from "@/shared/classPresentation";

export default {
  name: "ClassAttendanceTab",

  props: {
    groupId: {
      type: [String, Number],
      required: true,
    },
    members: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: Object,
      required: true,
    },
    groupLevel: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      availableMonths: [],
      selectedMonthKey: null,
      lessons: [],
      selectedLessonId: null,
      draftsByLesson: {},
      snapshotsByLesson: {},
      subjectOptions: [],
      loadingSubjects: false,
      savingSubject: false,
      lessonSubjectId: null,
    };
  },

  computed: {
    monthOptions() {
      return this.availableMonths.map((item) => ({
        text: item.label,
        value: item.key,
      }));
    },
    lessonOptions() {
      return this.lessons.map((lesson) => ({
        text: `${this.formatLessonDate(lesson.eventDate)} · ${lesson.startsAt}${
          lesson.isFuture ? " (futura)" : ""
        }`,
        value: lesson.id,
      }));
    },
    selectedLesson() {
      return (
        this.lessons.find((lesson) => lesson.id === this.selectedLessonId) ||
        null
      );
    },
    rosterMembers() {
      if (!this.selectedLesson) {
        return [];
      }

      const participants = this.selectedLesson.participants || [];

      if (!participants.length) {
        return this.members;
      }

      return this.members.filter((member) => participants.includes(member.id));
    },
    isDirty() {
      if (!this.selectedLesson) {
        return false;
      }

      const snapshot = this.snapshotsByLesson[this.selectedLesson.id] || {};

      return this.rosterMembers.some(
        (member) => this.statusOf(member.id) !== (snapshot[member.id] ?? null)
      );
    },
  },

  watch: {
    selectedLessonId: {
      immediate: true,
      handler() {
        this.lessonSubjectId = this.selectedLesson
          ? this.selectedLesson.courseClassId || null
          : null;
      },
    },
  },

  async created() {
    await Promise.all([this.load(), this.loadSubjects()]);
  },

  methods: {
    async loadSubjects() {
      if (this.groupLevel === null || this.groupLevel === undefined) {
        return;
      }

      this.loadingSubjects = true;

      try {
        const res = await partnerApi.getClassContentByLevel(this.groupLevel);
        const items = Array.isArray(res.data) ? res.data : res.data?.data || [];
        this.subjectOptions = items.map((item) => ({
          id: item.id,
          title: item.title,
        }));
      } catch (error) {
        this.subjectOptions = [];
      } finally {
        this.loadingSubjects = false;
      }
    },
    async changeSubject(courseClassId) {
      if (
        !this.selectedLesson ||
        !courseClassId ||
        courseClassId === this.selectedLesson.courseClassId
      ) {
        return;
      }

      this.savingSubject = true;

      try {
        const res = await partnerApi.updateLessonSubject(
          this.groupId,
          this.selectedLesson.id,
          courseClassId
        );

        const index = this.lessons.findIndex(
          (item) => item.id === this.selectedLesson.id
        );

        if (index !== -1) {
          this.$set(this.lessons, index, {
            ...this.lessons[index],
            courseClassId: res.data?.courseClassId ?? courseClassId,
            title: res.data?.title ?? this.lessons[index].title,
          });
        }

        this.$root.$emit("display-alert", {
          text: "Assunto da aula atualizado.",
          type: 0,
        });
      } catch (error) {
        // reverte a seleção visual para o valor atual da aula
        this.lessonSubjectId = this.selectedLesson.courseClassId || null;
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            "Nao foi possivel atualizar o assunto da aula.",
          type: 1,
        });
      } finally {
        this.savingSubject = false;
      }
    },
    async load(monthKey) {
      if (!this.members.length) {
        return;
      }

      this.loading = true;

      try {
        const period = this.parseMonthKey(monthKey || this.selectedMonthKey);
        const res = await partnerApi.getGroupLessonRoster(this.groupId, {
          month: period?.month,
          year: period?.year,
        });

        this.applyRoster(res.data);
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: "Nao foi possivel carregar a chamada da turma.",
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    applyRoster(data) {
      this.availableMonths = Array.isArray(data?.availableMonths)
        ? data.availableMonths
        : [];
      this.selectedMonthKey = this.buildMonthKey(data?.month, data?.year);
      this.lessons = Array.isArray(data?.lessons) ? data.lessons : [];
      this.draftsByLesson = {};
      this.snapshotsByLesson = {};

      this.lessons.forEach((lesson) => this.hydrateLesson(lesson));

      const defaultLesson = this.pickDefaultLesson();
      this.selectedLessonId = defaultLesson ? defaultLesson.id : null;
    },
    hydrateLesson(lesson) {
      const snapshot = {};

      (lesson.attendances || []).forEach((entry) => {
        snapshot[entry.customerId] =
          entry.status === undefined || entry.status === null
            ? null
            : +entry.status;
      });

      this.$set(this.snapshotsByLesson, lesson.id, snapshot);
      this.$set(this.draftsByLesson, lesson.id, { ...snapshot });
    },
    pickDefaultLesson() {
      const pastLessons = this.lessons.filter((lesson) => !lesson.isFuture);

      if (pastLessons.length) {
        return pastLessons[pastLessons.length - 1];
      }

      return this.lessons[0] || null;
    },
    statusOf(customerId) {
      if (!this.selectedLesson) {
        return null;
      }

      const draft = this.draftsByLesson[this.selectedLesson.id] || {};
      const status = draft[customerId];

      return status === undefined || status === null ? null : +status;
    },
    savedStatusOf(customerId) {
      if (!this.selectedLesson) {
        return null;
      }

      const snapshot = this.snapshotsByLesson[this.selectedLesson.id] || {};
      const status = snapshot[customerId];

      return status === undefined || status === null ? null : +status;
    },
    setStatus(customerId, status) {
      if (!this.selectedLesson || this.selectedLesson.isFuture) {
        return;
      }

      const draft = this.draftsByLesson[this.selectedLesson.id] || {};
      this.$set(draft, customerId, status);
      this.$set(this.draftsByLesson, this.selectedLesson.id, draft);
    },
    markAll(status) {
      if (!this.selectedLesson || this.selectedLesson.isFuture) {
        return;
      }

      this.rosterMembers.forEach((member) => this.setStatus(member.id, status));
    },
    async handleMonthChange() {
      await this.load(this.selectedMonthKey);
    },
    handleLessonChange() {
      // draft/snapshot already hydrated per lesson; nothing else to do
    },
    async save() {
      if (!this.selectedLesson || this.selectedLesson.isFuture) {
        return;
      }

      const draft = this.draftsByLesson[this.selectedLesson.id] || {};
      const attendances = this.rosterMembers
        .map((member) => ({
          customerId: member.id,
          status: draft[member.id],
        }))
        .filter((entry) => entry.status !== undefined && entry.status !== null);

      if (!attendances.length) {
        this.$root.$emit("display-alert", {
          text: "Marque a presença de pelo menos um aluno antes de salvar.",
          type: 1,
        });
        return;
      }

      this.saving = true;

      try {
        const res = await partnerApi.saveLessonAttendance(
          this.groupId,
          this.selectedLesson.id,
          { attendances }
        );

        this.applyLessonUpdate(res.data);

        this.$root.$emit("display-alert", {
          text: "Chamada salva com sucesso.",
          type: 0,
        });
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            "Nao foi possivel salvar a chamada.",
          type: 1,
        });
      } finally {
        this.saving = false;
      }
    },
    applyLessonUpdate(lesson) {
      if (!lesson?.id) {
        return;
      }

      const index = this.lessons.findIndex((item) => item.id === lesson.id);

      if (index !== -1) {
        this.$set(this.lessons, index, lesson);
      }

      this.hydrateLesson(lesson);
    },
    buildMonthKey(month, year) {
      if (!month || !year) {
        return null;
      }

      return `${year}-${String(month).padStart(2, "0")}`;
    },
    parseMonthKey(monthKey) {
      if (!monthKey || !String(monthKey).includes("-")) {
        return null;
      }

      const [year, month] = String(monthKey).split("-").map(Number);

      if (!year || !month) {
        return null;
      }

      return { month, year };
    },
    initials(name) {
      return initialsFromName(name);
    },
    formatLessonDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return new Intl.DateTimeFormat("pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      })
        .format(date)
        .replace(".", "");
    },
  },
};
</script>

<style scoped>
.attendance-tab__intro {
  margin-bottom: 4px;
}

.attendance-tab__hint {
  color: rgba(31, 41, 55, 0.72);
  font-size: 0.92rem;
  margin-top: 4px;
}

.attendance-tab__lesson-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.attendance-tab__lesson-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}

.attendance-tab__lesson-date {
  font-size: 0.85rem;
  color: rgba(31, 41, 55, 0.64);
  text-transform: capitalize;
}

.attendance-tab__quick {
  display: flex;
  gap: 4px;
}

.attendance-tab__roster {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attendance-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #ffffff;
}

.attendance-row__name {
  font-weight: 700;
  color: #1f2937;
}

.attendance-row__subject {
  display: flex;
  align-items: center;
  margin-top: 2px;
  font-size: 0.76rem;
  color: #94a3b8;
}

.attendance-row__subject--done {
  color: #16a34a;
}

.attendance-row__initials {
  font-size: 0.8rem;
  font-weight: 700;
}

.attendance-row__toggle {
  display: flex;
  align-items: center;
}

.attendance-row__btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
}

.attendance-tab__course-hint {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #166534;
}

.attendance-tab__unsaved {
  color: rgba(31, 41, 55, 0.72);
}

@media (max-width: 600px) {
  .attendance-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .attendance-row__toggle {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
